import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaCheckCircle, FaTimesCircle, FaEye, FaPlayCircle, FaBookOpen, FaGlobe, FaLayerGroup, FaUserGraduate, FaDollarSign, FaFileVideo, FaCalendarAlt, FaPlus, FaTimes, FaExclamationCircle } from 'react-icons/fa';
import DataTable from '../shared/DataTable';
import Loader from '../../shared/Loader';
import { showSuccess, showError, showConfirm, showLoading, closeLoading } from '../../../utils/Alert';
import './CourseManagement.css';

const CourseManagement = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('Pending');
    const [viewingCourse, setViewingCourse] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);

    useEffect(() => {
        if (isViewModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isViewModalOpen]);

    const openViewModal = (course) => {
        setViewingCourse(course);
        setIsViewModalOpen(true);
    };

    const fetchCourses = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/admin/courses', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCourses(res.data);
            setLoading(false);
        } catch (err) {
            showError('Fetch Failed', 'Could not load courses from server.');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCourses();
    }, []);

    const handleApprove = async (id, title) => {
        const result = await showConfirm('Approve Course?', `Are you sure you want to publish "${title}" for students?`);
        if (result.isConfirmed) {
            showLoading('Approving Course...');
            try {
                const token = localStorage.getItem('token');
                await axios.put(`/api/admin/courses/${id}/status`, { status: 'Active' }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                closeLoading();
                showSuccess('Course Published', 'The content is now live on the platform.');
                fetchCourses();
            } catch (err) {
                closeLoading();
                showError('Action Failed', 'Could not approve course.');
            }
        }
    };

    const handleReject = async (id, title) => {
        const { value: feedback } = await Swal.fire({
            title: 'Reject Course Submission',
            text: `Provide constructive feedback to the tutor on what needs fixing in "${title}":`,
            input: 'textarea',
            inputPlaceholder: 'e.g. The audio quality in the promo video is too low, and the syllabus is missing week 2 details.',
            showCancelButton: true,
            confirmButtonColor: '#EF4444',
            cancelButtonColor: '#64748B',
            confirmButtonText: 'Submit Rejection',
            inputValidator: (value) => {
                if (!value) {
                    return 'You must provide feedback for a rejection!';
                }
            }
        });

        if (feedback) {
            showLoading('Logging Rejection...');
            try {
                const token = localStorage.getItem('token');
                await axios.put(`/api/admin/courses/${id}/status`, { status: 'Rejected', adminFeedback: feedback }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                closeLoading();
                showSuccess('Rejection Logged', 'The tutor has been notified and access restricted.');
                fetchCourses();
            } catch (err) {
                closeLoading();
                showError('Action Failed', 'Could not reject course.');
            }
        }
    };

    const columns = [
        {
            header: 'Course Detail',
            key: 'title',
            width: '35%',
            render: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="course-icon-box" style={{ overflow: 'hidden' }}>
                        {row.thumbnailUrl ? (
                            <img src={row.thumbnailUrl.startsWith('http') ? row.thumbnailUrl : `${axios.defaults.baseURL || ''}${row.thumbnailUrl}`} alt="Thumb" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            <FaPlayCircle />
                        )}
                    </div>
                    <div>
                        <div style={{ fontWeight: 600, color: 'var(--primary-blue)' }}>{row.title}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{row.modules?.length || 0} Modules • {row.pricingType}</div>
                    </div>
                </div>
            )
        },
        {
            header: 'Tutor',
            key: 'tutorId',
            width: '20%',
            render: (row) => row.tutorId ? row.tutorId.name : 'Unknown'
        },
        {
            header: 'Status',
            key: 'status',
            width: '15%',
            render: (row) => (
                <span className={`status-badge ${row.status.toLowerCase()}`}>
                    {row.status.toUpperCase()}
                </span>
            )
        },
        {
            header: activeTab === 'Active' ? 'Pricing' : 'Submission Date',
            key: 'dateInfo',
            width: '15%',
            render: (row) => activeTab === 'Active' ? (row.pricingType === 'Free' ? 'Free' : `$${row.price}`) : new Date(row.createdAt).toLocaleDateString()
        },
        {
            header: 'Actions',
            key: 'actions',
            width: '15%',
            render: (row) => (
                <div className="actions-cell">
                    {/* View Button */}
                    <button className="icon-btn view-btn" title="View Curriculum" onClick={() => openViewModal(row)}><FaEye /></button>

                    {row.status === 'Pending' && (
                        <>
                            <button className="icon-btn approve-btn" onClick={() => handleApprove(row._id, row.title)} title="Approve Course"><FaCheckCircle /></button>
                            <button className="icon-btn reject-btn" onClick={() => handleReject(row._id, row.title)} title="Return with Feedback"><FaTimesCircle /></button>
                        </>
                    )}
                </div>
            )
        }
    ];

    const displayedCourses = courses.filter(c => c.status === activeTab);

    if (loading) {
        return <Loader />;
    }

    return (
        <div className="course-management fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Course Management</h1>
                <p className="page-subtitle">Review tutor submissions, manage platform contents and track enrollment metrics.</p>
            </div>

            <div className="premium-card stats-strip">
                <div className="stat-pill">
                    <span className="pill-label">Active Courses</span>
                    <span className="pill-value">{courses.filter(c => c.status === 'Active').length}</span>
                </div>
                <div className="stat-pill">
                    <span className="pill-label">Pending Reviews</span>
                    <span className="pill-value yellow">{courses.filter(c => c.status === 'Pending').length}</span>
                </div>
                <div className="stat-pill">
                    <span className="pill-label">Total Platform Courses</span>
                    <span className="pill-value blue">
                        {courses.length}
                    </span>
                </div>
            </div>

            <div className="management-tabs">
                <button
                    className={`tab-item ${activeTab === 'Pending' ? 'selected' : ''}`}
                    onClick={() => setActiveTab('Pending')}
                >
                    Pending Approvals
                </button>
                <button
                    className={`tab-item ${activeTab === 'Active' ? 'selected' : ''}`}
                    onClick={() => setActiveTab('Active')}
                >
                    Active Content
                </button>
                <button
                    className={`tab-item ${activeTab === 'Rejected' ? 'selected' : ''}`}
                    onClick={() => setActiveTab('Rejected')}
                >
                    Returned for Edits
                </button>
            </div>

            <DataTable
                title={`${activeTab} Course Submissions`}
                columns={columns}
                data={displayedCourses}
                filterOptions={[]}
            />

            {/* HIGHLY STYLED COURSE VIEW MODAL */}
            {isViewModalOpen && viewingCourse && createPortal(
                <div className={`modal-overlay ${isViewModalOpen ? 'open' : ''}`} onClick={() => setIsViewModalOpen(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', height: '85vh' }}>
                        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #002C5F, #00458E)', color: 'white' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <div style={{ background: 'rgba(255,255,255,0.2)', padding: '10px', borderRadius: '12px' }}>
                                    <FaBookOpen size={24} />
                                </div>
                                <div>
                                    <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}>{viewingCourse.title}</h2>
                                    <span style={{ fontSize: '0.9rem', opacity: 0.8 }}>Course Blueprint & Syllabus</span>
                                </div>
                            </div>
                            <button className="modal-close-btn" onClick={() => setIsViewModalOpen(false)}><FaTimes /></button>
                        </div>

                        <div className="modal-body" style={{ background: '#f8fafc', padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <div className="view-modal-layout" style={{ display: 'flex', flexWrap: 'nowrap', height: '100%', overflow: 'hidden' }}>
                                {/* Left Sidebar: Media Content - FIXED */}
                                <div style={{ flex: '0 0 400px', padding: '24px', background: 'white', borderRight: '1px solid #e2e8f0', overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                    <h4 className="premium-label" style={{ marginBottom: '15px', color: '#64748b' }}>COURSE PREVIEW MEDIA</h4>

                                    <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }} className="custom-scrollbar">
                                        <div className="thumbnail-preview" style={{ marginBottom: '25px', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', background: '#000', position: 'relative' }}>
                                            <label className="premium-label" style={{ position: 'absolute', zIndex: 10, background: 'rgba(0,0,0,0.6)', color: 'white', padding: '4px 12px', borderRadius: '0 0 12px 0', fontSize: '11px' }}>REPRESENTATIVE IMAGE</label>
                                            <img
                                                src={viewingCourse.thumbnailUrl ? (viewingCourse.thumbnailUrl.startsWith('http') ? viewingCourse.thumbnailUrl : `${axios.defaults.baseURL || ''}${viewingCourse.thumbnailUrl}`) : 'https://via.placeholder.com/800x450?text=No+Thumbnail'}
                                                alt="Thumbnail"
                                                style={{ width: '100%', height: 'auto', display: 'block' }}
                                            />
                                        </div>

                                        {viewingCourse.promotionalVideoUrl && (
                                            <div className="video-preview" style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', background: '#000', position: 'relative' }}>
                                                <label className="premium-label" style={{ position: 'absolute', zIndex: 10, background: 'rgba(0,0,0,0.6)', color: 'white', padding: '4px 12px', borderRadius: '0 0 12px 0', fontSize: '11px' }}>PROMOTIONAL VIDEO</label>
                                                <video controls style={{ width: '100%', display: 'block' }}>
                                                    <source src={viewingCourse.promotionalVideoUrl.startsWith('http') ? viewingCourse.promotionalVideoUrl : `${axios.defaults.baseURL || ''}${viewingCourse.promotionalVideoUrl}`} type="video/mp4" />
                                                    Your browser does not support the video tag.
                                                </video>
                                            </div>
                                        )}

                                        {!viewingCourse.promotionalVideoUrl && (
                                            <div style={{ padding: '30px', textAlign: 'center', background: '#f1f5f9', borderRadius: '16px', border: '1px dashed #cbd5e1' }}>
                                                <FaFileVideo size={30} color="#94a3b8" />
                                                <p style={{ margin: '10px 0 0 0', color: '#64748b', fontSize: '13px' }}>No promotional video uploaded for this course.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Right Content: Course Details - SCROLLABLE */}
                                <div style={{ flex: '1', padding: '30px', overflowY: 'auto' }}>
                                    {viewingCourse.status === 'Rejected' && viewingCourse.adminFeedback && (
                                        <div style={{ padding: '16px', background: '#FEF2F2', border: '1px solid #F87171', borderRadius: '12px', marginBottom: '24px', display: 'flex', gap: '12px' }}>
                                            <FaExclamationCircle color="#DC2626" size={24} style={{ flexShrink: 0, marginTop: '2px' }} />
                                            <div>
                                                <h4 style={{ margin: '0 0 4px 0', color: '#991B1B', fontSize: '14px' }}>Return for Edit Feedback</h4>
                                                <p style={{ margin: 0, color: '#7F1D1D', fontSize: '14px', lineHeight: 1.5 }}>
                                                    {viewingCourse.adminFeedback}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                                        <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}><FaGlobe /> CATEGORY</div>
                                            <div style={{ color: '#0f172a', fontWeight: 600, marginTop: '4px' }}>{viewingCourse.category}</div>
                                        </div>
                                        <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}><FaLayerGroup /> DIFFICULTY</div>
                                            <div style={{ color: '#0f172a', fontWeight: 600, marginTop: '4px' }}>{viewingCourse.difficultyLevel}</div>
                                        </div>
                                        <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}><FaUserGraduate /> LANGUAGE</div>
                                            <div style={{ color: '#0f172a', fontWeight: 600, marginTop: '4px' }}>{viewingCourse.language}</div>
                                        </div>
                                        <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}><FaDollarSign /> PRICING</div>
                                            <div style={{ color: viewingCourse.pricingType === 'Free' ? '#10B981' : '#B45309', fontWeight: 700, marginTop: '4px' }}>
                                                {viewingCourse.pricingType === 'Free' ? 'FREE' : `$ ${viewingCourse.price}`}
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '30px' }}>
                                        <h4 className="premium-label" style={{ borderBottom: '2px solid #002C5F', paddingBottom: '6px', display: 'inline-block' }}>ABOUT THIS COURSE</h4>
                                        <p style={{ color: '#334155', lineHeight: 1.6, marginTop: '12px', fontSize: '15px' }}>{viewingCourse.description}</p>
                                    </div>

                                    <div style={{ marginBottom: '30px' }}>
                                        <h4 className="premium-label">PREREQUISITES</h4>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                                            {viewingCourse.prerequisites && viewingCourse.prerequisites.length > 0 ? viewingCourse.prerequisites.map((p, i) => (
                                                <span key={i} style={{ background: '#e0f2fe', color: '#0369a1', padding: '6px 14px', borderRadius: '30px', fontSize: '13px', fontWeight: 500 }}>{p}</span>
                                            )) : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No specific prerequisites listed.</span>}
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '30px' }}>
                                        <h4 className="premium-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>COURSE CURRICULUM</span>
                                            <span style={{ color: '#64748b', fontSize: '12px' }}>{viewingCourse.modules?.length || 0} Modules Total</span>
                                        </h4>
                                        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            {viewingCourse.modules?.map((m, i) => (
                                                <div key={i} style={{ padding: '16px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                                    <div style={{ fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <span style={{ background: '#002C5F', color: 'white', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>{i + 1}</span>
                                                        {m.moduleTitle}
                                                    </div>
                                                    <div style={{ color: '#64748b', fontSize: '13px', marginTop: '6px', paddingLeft: '34px' }}>{m.moduleDescription}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '20px' }}>
                                        <h4 className="premium-label">AVAILABLE BATCHES</h4>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginTop: '12px' }}>
                                            {viewingCourse.batches?.map((b, i) => (
                                                <div key={i} style={{ padding: '16px', border: '1px solid #e2e8f0', background: 'white', borderRadius: '12px', borderLeft: '4px solid #D4AF37' }}>
                                                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{b.batchName}</div>
                                                    <div style={{ display: 'flex', color: '#64748b', fontSize: '12px', marginTop: '8px', gap: '12px' }}>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><FaCalendarAlt size={10} /> {new Date(b.startDate).toLocaleDateString()}</span>
                                                        <span>-</span>
                                                        <span>{new Date(b.endDate).toLocaleDateString()}</span>
                                                    </div>
                                                    <div style={{ color: '#002C5F', fontSize: '12px', fontWeight: 600, marginTop: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                        <FaPlus size={8} /> Max Intake: {b.maxStudents} Students
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={{ padding: '20px', borderTop: '1px solid #e2e8f0', background: 'white', display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px', borderRadius: '12px' }}>
                                        <button className="secondary-btn" onClick={() => setIsViewModalOpen(false)}>Close View</button>
                                        {viewingCourse.status === 'Pending' && (
                                            <>
                                                <button className="primary-btn" style={{ background: '#EF4444' }} onClick={() => { setIsViewModalOpen(false); handleReject(viewingCourse._id, viewingCourse.title); }}><FaTimesCircle style={{ marginRight: '8px' }} /> Reject Course</button>
                                                <button className="primary-btn" style={{ background: '#10B981', border: 'none' }} onClick={() => { setIsViewModalOpen(false); handleApprove(viewingCourse._id, viewingCourse.title); }}><FaCheckCircle style={{ marginRight: '8px' }} /> Approve Live</button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default CourseManagement;
